import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";
import { ArrowRight, ChevronRight, Zap, Battery, Construction, Activity, ShieldCheck, MapPin, Building2, Truck, Award } from "lucide-react";
import { IMAGES, SERVICES, PROJECTS, COMPANY } from "@/lib/site-data";
import { Container, Section, SectionTitle, Eyebrow, Card, CTABand } from "@/components/ui-bits/Primitives";

function Counter({ to, suffix = "", duration = 2 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true });
    const mv = useMotionValue(0);
    const rounded = useTransform(mv, (v) => Math.floor(v).toLocaleString());
    useEffect(() => { if (inView) animate(mv, to, { duration, ease: "easeOut" }); }, [inView, to, mv, duration]);
    return <span ref={ref}><motion.span>{rounded}</motion.span>{suffix}</span>;
}

const HOMEHERO_IMAGES = [IMAGES.heroFastCharging, IMAGES.solarCanopy, IMAGES.chargingPark];

export default function Home() {
    const [heroIdx, setHeroIdx] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setHeroIdx((i) => (i + 1) % HOMEHERO_IMAGES.length), 7000);
        return () => clearInterval(t);
    }, []);

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-center overflow-hidden -mt-16 md:-mt-24 pt-16 md:pt-24">
                {HOMEHERO_IMAGES.map((src, i) => (
                    <motion.div
                        key={src}
                        className="absolute inset-0"
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: heroIdx === i ? 1 : 0, scale: heroIdx === i ? 1 : 1.05 }}
                        transition={{ duration: 1.4 }}
                    >
                        <img src={src} alt="EV charging infrastructure" className="w-full h-full object-cover" />
                    </motion.div>
                ))}
                <div className="absolute inset-0 hero-overlay" />
                <div className="absolute inset-0 hero-overlay-bottom" />
                <div className="absolute inset-0 grid-bg opacity-25" />
                <div className="absolute right-0 top-0 bottom-0 w-1/2 bg-gradient-to-l from-electric-400/[0.06] to-transparent" />

                <Container className="relative z-10 w-full">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        <div className="inline-flex items-center gap-2 border border-electric-400/40 bg-electric-400/5 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] text-electric-400 mb-8">
                            <ShieldCheck className="w-3.5 h-3.5" /> Veteran-Owned · Texas & Nationwide
                        </div>
                        <h1 className="font-heading font-medium text-white text-5xl md:text-6xl lg:text-[78px] leading-[0.96] tracking-tight max-w-4xl">
                            Powering <span className="text-electric-400">Texas'</span> EV Future
                        </h1>
                        <p className="mt-8 text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl">
                            M-Class EV Charging develops, installs, owns, and operates Level 2 and DC Fast Charging infrastructure for municipalities, commercial property owners, fleets, multifamily developments, and public-private partnerships.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link to="/contact" data-testid="hero-cta-consultation" className="btn-primary">Request a Consultation <ArrowRight className="w-4 h-4" /></Link>
                            <Link to="/services" data-testid="hero-cta-solutions" className="btn-secondary">Explore Solutions</Link>
                            <Link to="/site-host" data-testid="hero-cta-sitehost" className="btn-ghost">Become a Site Host <ChevronRight className="w-4 h-4" /></Link>
                        </div>
                    {/* Stats strip — inline below CTAs to avoid overlap */}
                    <div className="mt-16 md:mt-24 hidden md:block">
                        <div className="border-t border-white/10 pt-6 grid grid-cols-4 gap-8 max-w-4xl">
                            {[
                                { k: "150–400kW", v: "DC Fast Range" },
                                { k: "64+", v: "Stations Planned" },
                                { k: "OCPP 2.0.1", v: "Network Ready" },
                                { k: "100%", v: "American Vendors" },
                            ].map((s, i) => (
                                <motion.div key={s.v} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }}>
                                    <div className="font-heading text-2xl md:text-3xl text-white tracking-tight">{s.k}</div>
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mt-1">{s.v}</div>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* Hero indicator dots */}
                    <div className="absolute bottom-10 right-6 md:right-8 lg:right-12 hidden md:flex flex-col gap-2">
                        {HOMEHERO_IMAGES.map((_, i) => (
                            <button key={i} onClick={() => setHeroIdx(i)} aria-label={`Slide ${i+1}`}
                                className={`w-8 h-[2px] transition-colors ${heroIdx === i ? "bg-electric-400" : "bg-white/20"}`} />
                        ))}
                    </div>
                    </motion.div>
                </Container>
            </section>

            {/* MISSION */}
            <Section className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-7">
                            <Eyebrow className="mb-4">Our Mission</Eyebrow>
                            <h2 className="font-heading font-medium text-white text-3xl md:text-5xl leading-[1.05] tracking-tight">
                                Turnkey infrastructure that creates <span className="text-electric-400">long-term economic growth</span> for the communities we serve.
                            </h2>
                        </div>
                        <div className="lg:col-span-5">
                            <p className="text-slate-400 text-base md:text-lg leading-relaxed">
                                We develop, build, own and operate the charging assets — handling grants, utility coordination, engineering, permitting, construction, and 24/7 operations — so property owners and municipalities don't have to.
                            </p>
                            <Link to="/about" className="btn-ghost mt-6 inline-flex" data-testid="mission-learn-more">Our Story <ChevronRight className="w-4 h-4" /></Link>
                        </div>
                    </div>
                </Container>
            </Section>

            {/* SERVICES BENTO */}
            <Section id="solutions" className="border-t border-white/5">
                <Container>
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <SectionTitle kicker="Solutions" title="Built for every charging mission." />
                        <Link to="/services" data-testid="services-view-all" className="btn-ghost">View all <ChevronRight className="w-4 h-4" /></Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {SERVICES.map((s) => {
                            const Icon = s.icon;
                            return (
                                <Card key={s.id} data-testid={`service-card-${s.id}`} className="flex flex-col !p-7">
                                    <Icon className="w-8 h-8 text-electric-400 mb-5" strokeWidth={1.5} />
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">{s.kicker}</div>
                                    <h3 className="font-heading font-medium text-lg text-white mb-3">{s.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-1">{s.copy}</p>
                                    <Link to={`/services#${s.id}`} className="text-electric-400 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1 hover:gap-2 transition-all">Learn more <ArrowRight className="w-3.5 h-3.5" /></Link>
                                </Card>
                            );
                        })}
                        <Link to="/services" data-testid="services-all-cta-card" className="relative bg-electric-400/5 backdrop-blur-xl border border-electric-400/40 p-7 transition-all duration-300 hover:bg-electric-400/10 hover:-translate-y-1 flex flex-col justify-between group">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.2em] text-electric-400 mb-2">All Solutions</div>
                                <h3 className="font-heading font-medium text-lg text-white mb-3">Explore the full M-Class stack</h3>
                                <p className="text-sm text-slate-400 leading-relaxed">From DC fast and MCS to marine and aviation — see how it fits together.</p>
                            </div>
                            <span className="text-electric-400 text-xs uppercase tracking-[0.18em] inline-flex items-center gap-1 group-hover:gap-2 transition-all mt-6">View all <ArrowRight className="w-3.5 h-3.5" /></span>
                        </Link>
                    </div>
                </Container>
            </Section>

            {/* WHO WE SERVE */}
            <Section className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <Eyebrow className="mb-4">Who We Serve</Eyebrow>
                            <h2 className="font-heading font-medium text-white text-3xl md:text-4xl tracking-tight leading-[1.1]">From municipal corridors to fleet depots — we deliver the infrastructure investors and agencies trust.</h2>
                            <div className="mt-8 flex flex-col gap-3">
                                <Link to="/commercial" data-testid="serve-commercial-link" className="btn-secondary justify-between"><span>Commercial Property Solutions</span><ArrowRight className="w-4 h-4" /></Link>
                                <Link to="/fleet" data-testid="serve-fleet-link" className="btn-secondary justify-between"><span>Fleet Charging Solutions</span><ArrowRight className="w-4 h-4" /></Link>
                                <Link to="/grants" data-testid="serve-grants-link" className="btn-secondary justify-between"><span>Grant & Funding Programs</span><ArrowRight className="w-4 h-4" /></Link>
                            </div>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-2 gap-4">
                            {[
                                { img: IMAGES.commercial, label: "Commercial", icon: Building2 },
                                { img: IMAGES.fleet, label: "Fleet & Logistics", icon: Truck },
                                { img: IMAGES.cityNight, label: "Municipal", icon: MapPin },
                                { img: IMAGES.apartment, label: "Multifamily", icon: Building2 },
                            ].map((b, i) => {
                                const Icon = b.icon;
                                return (
                                    <motion.div key={b.label} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className={`relative overflow-hidden aspect-[4/5] group ${i % 3 === 0 ? "md:translate-y-6" : ""}`}>
                                        <img src={b.img} alt={b.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-ink-900 via-ink-900/40 to-transparent" />
                                        <div className="absolute bottom-0 left-0 right-0 p-5 flex items-center gap-2">
                                            <Icon className="w-4 h-4 text-electric-400" />
                                            <span className="text-sm uppercase tracking-[0.18em] text-white">{b.label}</span>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </div>
                </Container>
            </Section>

            {/* FEATURED PROJECT */}
            <Section className="border-t border-white/5">
                <Container>
                    <SectionTitle kicker="Featured Projects" title="Infrastructure shaping Texas corridors." className="mb-12" />
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {PROJECTS.map((p) => (
                            <Card key={p.id} data-testid={`featured-project-${p.id}`} className="!p-0 overflow-hidden flex flex-col">
                                <div className="relative h-56 overflow-hidden">
                                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                                    <div className="absolute top-4 left-4 bg-electric-400 text-ink-900 text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1">{p.status}</div>
                                </div>
                                <div className="p-7 flex-1 flex flex-col">
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-2">{p.location}</div>
                                    <h3 className="font-heading font-medium text-xl text-white mb-3">{p.title}</h3>
                                    <p className="text-sm text-slate-400 leading-relaxed mb-6 flex-1">{p.desc}</p>
                                    <div className="grid grid-cols-2 gap-3 mb-2 border-t border-white/10 pt-4">
                                        {p.stats.slice(0, 4).map((s) => (
                                            <div key={s.v}>
                                                <div className="font-heading text-lg text-electric-400">{s.k}</div>
                                                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">{s.v}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                    <div className="mt-10"><Link to="/projects" data-testid="home-projects-all" className="btn-ghost">All projects <ChevronRight className="w-4 h-4" /></Link></div>
                </Container>
            </Section>

            {/* IMPACT */}
            <Section className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5">
                            <Eyebrow className="mb-4">Impact</Eyebrow>
                            <h2 className="font-heading font-medium text-white text-3xl md:text-4xl tracking-tight leading-[1.1]">Measurable progress, mile by mile.</h2>
                            <p className="mt-6 text-slate-400 leading-relaxed">Every site we deploy contributes to cleaner air, stronger local economies, and equitable access to reliable transportation across Texas.</p>
                            <Link to="/sustainability" data-testid="impact-learn" className="btn-secondary mt-8">Sustainability Report <ArrowRight className="w-4 h-4" /></Link>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-2 gap-6">
                            {[
                                { v: 184000, s: "+", l: "kWh Delivered" },
                                { v: 268000, s: " lbs", l: "CO₂ Offset" },
                                { v: 12400, s: "+", l: "Charging Sessions" },
                                { v: 2, s: "", l: "Communities Served" },
                            ].map((m) => (
                                <Card key={m.l} hover={false} className="!p-7">
                                    <div className="font-heading font-medium text-4xl md:text-5xl text-electric-400 tracking-tight">
                                        <Counter to={m.v} suffix={m.s} />
                                    </div>
                                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mt-2">{m.l}</div>
                                </Card>
                            ))}
                        </div>
                    </div>
                </Container>
            </Section>

            {/* FOUNDER PROMO */}
            <Section className="border-t border-white/5">
                <Container>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
                        <div className="lg:col-span-4">
                            <Eyebrow className="mb-4">Founder</Eyebrow>
                            <div className="font-heading text-2xl text-white">Omari Ross</div>
                            <div className="mt-1 text-electric-400 text-xs uppercase tracking-[0.22em]">Founder & CEO</div>
                            <div className="mt-6 text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Education</div>
                            <div className="text-sm text-slate-300 leading-relaxed">M.S. Biology · Material Science & Commercialization</div>
                            <div className="text-xs text-slate-500 mt-1">University of Houston · Texas State University</div>
                        </div>
                        <div className="lg:col-span-8">
                            <h2 className="font-heading font-medium text-white text-3xl md:text-4xl tracking-tight leading-[1.1]">"We build infrastructure that outlasts the headline — and serves the communities behind it."</h2>
                            <p className="mt-6 text-slate-400 leading-relaxed max-w-2xl">Focused expertise in electric vehicle charging systems (EVCS) — from site selection and utility coordination to deployment, network operations, and long-term performance of public and fleet charging infrastructure.</p>
                            <Link to="/about" data-testid="founder-readmore" className="btn-secondary mt-8">Read the Story <ArrowRight className="w-4 h-4" /></Link>
                        </div>
                    </div>
                </Container>
            </Section>

            <CTABand
                title="Ready to power your property, fleet, or community?"
                subtitle="Schedule a consultation or apply to become a site host — we'll handle grants, engineering, construction, and operations."
            />
        </div>
    );
}
